import React, { useEffect, useRef, useState } from "react";

import { PopupCard } from "./PopupCard";

import { useDismissPopup, usePopupDismissed } from "../../../api/hooks";
import { useFormAuthoringCoachEnabled } from "../hooks/useFormAuthoringCoachEnabled";
import { useIsAuthoringReviewForm } from "../hooks/useIsAuthoringReviewForm";

import { useAskPerformYardStore } from "../AskPerformYardStore";
import { FORM_AUTHORING_COACH_START_QUERY } from "../constants";

/** Gap between the top bar and the callout — the `mt-2` below, which offsetHeight doesn't include. */
const CALLOUT_TOP_GAP_PX = 8;

/** Half the caret's 6px base, so an offset can name where its tip lands rather than its left edge. */
const CARET_HALF_WIDTH_PX = 3;
/** Keeps the caret off the card's 8px rounded corner when the trigger is narrower than the card. */
const CARET_MIN_INSET_PX = 12;
/** Used until the trigger has been measured, and in environments that report no layout at all. */
const CARET_FALLBACK_INSET_PX = 24;

/**
 * Callout anchored to the Ask PerformYard top-nav trigger while an employee fills out a review
 * form, shown every visit until the user dismisses it with the x (persisted per-user).
 * Rendered by AskPyNavTrigger so it can only appear where the trigger it points at is mounted.
 */
export const FormAuthoringCoachPopup: React.FC = () => {
	const formAuthoringCoachEnabled = useFormAuthoringCoachEnabled();
	const isAuthoringReviewForm = useIsAuthoringReviewForm();
	const isPanelOpen = useAskPerformYardStore((state) => state.isPanelOpen);
	const openPanel = useAskPerformYardStore((state) => state.openPanel);
	const resetChat = useAskPerformYardStore((state) => state.resetChat);
	const setPendingFormAuthoringCoachStart = useAskPerformYardStore(
		(state) => state.setPendingFormAuthoringCoachStart
	);

	const eligible = formAuthoringCoachEnabled && isAuthoringReviewForm && !isPanelOpen;
	const { data: dismissalData } = usePopupDismissed("form-authoring-coach", eligible);
	const { mutate: dismissPopup } = useDismissPopup("form-authoring-coach");
	const visible = eligible && dismissalData?.dismissed === false;

	// The callout hangs off the top bar into the review page's controls column. Publish its presence
	// and measured height on <body> so review_layout.css can drop that column clear of it — neither
	// surface occludes the other, and both go back to normal the moment the callout is dismissed.
	const calloutRef = useRef<HTMLDivElement>(null);
	// Node 50:4823 points the caret at the middle of the trigger, not at a fixed inset. The callout is
	// right-aligned to that trigger and its offset parent wraps nothing else, so half the parent's
	// width is the trigger's midpoint — measured rather than derived from the label's text metrics.
	const [caretInsetPx, setCaretInsetPx] = useState(CARET_FALLBACK_INSET_PX);
	useEffect(() => {
		if (!visible) return;
		const callout = calloutRef.current;
		const height = callout?.offsetHeight ?? 0;
		document.body.style.setProperty("--pyo-ask-py-callout-offset", `${height + CALLOUT_TOP_GAP_PX}px`);
		document.body.dataset.askPyCallout = "visible";

		const triggerWidth = callout?.parentElement?.offsetWidth ?? 0;
		if (triggerWidth > 0) {
			const cardWidth = callout?.offsetWidth ?? 0;
			const maxInset = Math.max(cardWidth - CARET_MIN_INSET_PX, CARET_MIN_INSET_PX);
			setCaretInsetPx(Math.min(Math.max(triggerWidth / 2 - CARET_HALF_WIDTH_PX, CARET_MIN_INSET_PX), maxInset));
		}

		return () => {
			delete document.body.dataset.askPyCallout;
			document.body.style.removeProperty("--pyo-ask-py-callout-offset");
		};
	}, [visible]);

	if (!visible) {
		return null;
	}

	// The panel sends the query, not this callout: opening the panel unmounts the callout, and a
	// send from a component being unmounted gets aborted by its own cleanup.
	//
	// Each analysis is its own coaching session, so start a fresh conversation first. Without
	// this, the panel re-hydrates whatever conversation was last open and every click appends
	// another "Analyze my responses" turn to it. `resetChat` leaves the pending flag alone.
	const handleStart = (): void => {
		resetChat();
		openPanel();
		setPendingFormAuthoringCoachStart(true);
	};

	return (
		<div
			ref={calloutRef}
			// The shadow lives here rather than on the card so it traces the caret and card together;
			// on the card alone it would draw a line across the caret's base.
			className="hide-in-pdf absolute top-full right-0 z-[1099] mt-2 w-[280px] drop-shadow-[0_4px_30px_rgba(0,0,0,0.15)]"
			data-testid="form-authoring-coach-popup"
		>
			{/* Caret pointing back up at the trigger — node I50:4823;19581:14717, a 6x4 white
			    triangle. Borderless, and overlapping the card's top edge by half a pixel so the two
			    read as one continuous shape with no seam between them. */}
			<div
				className="absolute -top-[3.5px] size-0 border-x-[3px] border-b-[4px] border-solid border-x-transparent border-b-neutral-0"
				style={{ right: `${caretInsetPx}px` }}
				data-testid="form-authoring-coach-popup-caret"
			/>
			<PopupCard
				title="Want feedback on your responses?"
				action={{ label: FORM_AUTHORING_COACH_START_QUERY, onClick: handleStart }}
				onDismiss={() => dismissPopup()}
				withShadow={false}
			/>
		</div>
	);
};

export default FormAuthoringCoachPopup;
